import { IActiveCountry, IActiveDimension } from 'interfaces';
import React, { useState } from 'react';
import { IEventsData } from './EventsFeedInterfaces';
import { getFeedCluster } from './eventUtils';
import {
    FGStack,
    FGTile,
    FGTypography,
    FGButton,
    FGIcons,
    FGDropdownMenu,
    FGDropdownMenuListContent,
    FGDropdownMenuListItem,
    FGTag,
    TAGS_COLORS,
    TAGS_SIZES,
} from '@fg/design-system';
import { filter, map } from 'lodash/fp';
import { MOMENTJS_DATE_DISPLAY_FORMAT, C_SCORES_DATE_FORMAT } from '../../constants';
import ScoresImpact from './ScoresImpact';
import { CountryBadge } from './CountryBadge';
import cx from 'classnames';

type Props = IEventsData & {
    isActive: boolean;
    onClick: (id: number) => void;
    onDelete: (id: number) => void;
    dimensions: { [dimensionId: number]: IActiveDimension };
    canDeleteEvents: boolean;
    countriesById: { [countryID: number]: IActiveCountry };
    isGQInternalUser?: boolean;
    onDeleteAllClusterArticles?: (
        eventId: number,
        clusterId: string,
        clusterDate: string
    ) => Promise<void>;
    onDeleteAllClusterScores?: (clusterId: string) => Promise<void>;
};
const EventItem = React.memo((props: Props) => {
    const feedCluster = getFeedCluster(props.cluster);
    const isCluster = (feedCluster?.article_count ?? 0) > 1;
    const hasMachineScores = !!props.uscores?.some(score => !score.human);
    return (
        <FGTile
            data-testid="event-item"
            data-eventid={props.id}
            className={cx('event-item-container', {
                'active-event': props.isActive,
            })}
            alignItems="flex-start"
            onClick={() => props.onClick(props.id)}>
            <FGStack direction="column" gap={4} style={{ width: '100%' }}>
                <FGStack
                    gap={8}
                    style={{
                        flexWrap: 'nowrap',
                    }}
                    justifyContent="space-between"
                    alignItems="flex-start">
                    <FGStack gap={8} style={{ flexWrap: 'wrap' }}>
                        {isCluster && (
                            <FGTag
                                color={TAGS_COLORS.blue}
                                size={TAGS_SIZES.small}
                                label={`Multiple Articles (${feedCluster.article_count})`}
                            />
                        )}
                        {map(
                            (countryId: number) => {
                                const country: IActiveCountry =
                                    props.countriesById[countryId];
                                if (!country) {
                                    console.log('no country for id', countryId);
                                }
                                return (
                                    <CountryBadge
                                        country={country}
                                        key={countryId}
                                    />
                                );
                            },
                            filter(
                                countryId => !!props.countriesById[countryId],
                                props.countries
                            )
                        )}
                    </FGStack>
                    {props.canDeleteEvents && (
                        <div onClick={e => e.stopPropagation()}>
                            {isCluster ? (
                                <ClusterEventMenu
                                    articlesCount={feedCluster.article_count}
                                    hasMachineScores={hasMachineScores}
                                    onDeleteAllArticles={() =>
                                        props.onDeleteAllClusterArticles(
                                            props.id,
                                            feedCluster.id,
                                            props.time.format(
                                                C_SCORES_DATE_FORMAT
                                            )
                                        )
                                    }
                                    onDeleteAllMachineScores={() =>
                                        props.onDeleteAllClusterScores(
                                            feedCluster.id
                                        )
                                    }
                                />
                            ) : (
                                <EventMenu
                                    onDelete={() => props.onDelete(props.id)}
                                />
                            )}
                        </div>
                    )}
                </FGStack>
                <FGTypography
                    alignment="left"
                    variant="body3-long"
                    color="strong"
                    tag="label">
                    {props.headline}
                </FGTypography>
                <FGStack gap={8}>
                    <FGTypography
                        variant="body4"
                        tag="label"
                        color="weak"
                        cssStyle={{ whiteSpace: 'nowrap' }}>
                        {props.time.format(MOMENTJS_DATE_DISPLAY_FORMAT)}
                    </FGTypography>
                    <FGTypography tag="label" variant="body4" color="weak">
                        {/* todo: fix dot*/}•
                    </FGTypography>
                    <FGTypography variant="body4" tag="label" color="weak">
                        {props.source}
                    </FGTypography>
                </FGStack>
                <ScoresImpact
                    dimensions={props.dimensions}
                    scores={props.uscores}
                />
            </FGStack>
        </FGTile>
    );
});

const EventMenu = React.memo(({ onDelete }: { onDelete: () => void }) => {
    return (
        <FGDropdownMenu
            trigger="click"
            triggerElement={
                <FGButton
                    variant="ghost"
                    icon={<FGIcons icon="MenuDotsHorizontal" />}
                    aria-label="Event actions"
                    data-testid="event-actions"
                    title="Event actions"
                    style={{ height: 24, width: 24 }}
                />
            }>
            <FGDropdownMenuListContent>
                <FGDropdownMenuListItem>
                    <FGButton
                        variant="ghost"
                        icon={<FGIcons icon="TrashBin" />}
                        data-testid="event-delete"
                        onClick={onDelete}>
                        Delete
                    </FGButton>
                </FGDropdownMenuListItem>
            </FGDropdownMenuListContent>
        </FGDropdownMenu>
    );
});

const ClusterEventMenu = React.memo(
    ({
        articlesCount,
        hasMachineScores,
        onDeleteAllArticles,
        onDeleteAllMachineScores,
    }: {
        articlesCount?: number;
        hasMachineScores: boolean;
        onDeleteAllArticles: () => Promise<void>;
        onDeleteAllMachineScores: () => Promise<void>;
    }) => {
        const [isOpen, setIsOpen] = useState(false);
        const [scoresDeleted, setScoresDeleted] = useState(false);

        const handleDeleteAllArticles = async () => {
            try {
                await onDeleteAllArticles();
                setIsOpen(false);
            } catch (err) {
                console.log(err);
            }
        };

        const handleDeleteAllMachineScores = async () => {
            try {
                await onDeleteAllMachineScores();
                setScoresDeleted(true);
                setIsOpen(false);
            } catch (err) {
                console.log(err);
            }
        };

        return (
            <FGDropdownMenu
                visible={isOpen}
                onClickOutside={() => setIsOpen(false)}
                appendTo={() => document.body}
                triggerElement={
                    <FGButton
                        variant="ghost"
                        icon={<FGIcons icon="MenuDotsHorizontal" />}
                        aria-label="Cluster actions"
                        data-testid="event-cluster-actions"
                        title="Cluster actions"
                        style={{ height: 20, width: 20 }}
                        onClick={() => setIsOpen(prev => !prev)}
                    />
                }>
                <FGDropdownMenuListContent
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                    }}>
                    <FGDropdownMenuListItem>
                        <FGButton
                            variant="ghost"
                            fullWidth
                            icon={<FGIcons icon="TrashBin" />}
                            data-testid="event-cluster-delete-articles"
                            onClick={handleDeleteAllArticles}>
                            {`Delete all articles${
                                articlesCount ? ` (${articlesCount})` : ''
                            }`}
                        </FGButton>
                    </FGDropdownMenuListItem>
                    {!scoresDeleted && hasMachineScores && (
                        <FGDropdownMenuListItem>
                            <FGButton
                                variant="ghost"
                                fullWidth
                                icon={<FGIcons icon="TrashBin" />}
                                data-testid="event-cluster-delete-scores"
                                onClick={handleDeleteAllMachineScores}>
                                Delete all machine scores
                            </FGButton>
                        </FGDropdownMenuListItem>
                    )}
                </FGDropdownMenuListContent>
            </FGDropdownMenu>
        );
    }
);

export default EventItem;
